// src/pages/Register.tsx
import Seo from "../components/Seo";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useRegisterMutation } from "../app/api/authApi";
import type {
  ChildRegistrationInput,
  ParentRegisterRequest,
  StudentRegisterRequest,
} from "../app/api/authApi";
import {
  setCredentials,
  selectIsAuthenticated,
} from "../app/features/authSlice";

import { ArrowRight, Backpack, BookOpen, Bot, Briefcase, Coins, Hammer, Mic, TriangleAlert, Users } from "lucide-react";
import Ico from "../components/Ico";
/* ─────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────── */

// eslint-disable-next-line no-useless-escape
const EMAIL_RE = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

function ageFromDOB(dob: string): number {
  const diff = Date.now() - new Date(dob).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
}

function deriveTrackLabel(dob: string): string {
  if (!dob) return "";
  const age = ageFromDOB(dob);
  if (age >= 6 && age <= 9) return "Foundation Builders (Ages 6–9)";
  if (age > 9 && age <= 12) return "Explorers (Ages 9–12)";
  if (age > 12 && age <= 18) return "Innovators (Ages 13–18)";
  return age < 6 ? "Too young (min. age 6)" : "Too old (max. age 18)";
}

function isValidAge(dob: string): boolean {
  if (!dob) return false;
  const age = ageFromDOB(dob);
  return age >= 6 && age <= 18;
}

/* ─────────────────────────────────────────────────────────────────
   SHARED UI ATOMS
───────────────────────────────────────────────────────────────── */

function FSLMark() {
  return (
    <div className="flex items-center gap-2.5">
      <img src="/images/logo.png" alt="" className="h-9 w-9 rounded-[10px]" />
      <div>
        <p className="text-[13px] font-bold leading-tight tracking-[-0.03em] text-[#16213e]">
          Future Smart Lab
        </p>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#a6adbf]">
          Innovation Academy
        </p>
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  hint?: string;
  required?: boolean;
  min?: string;
  max?: string;
  rightElement?: React.ReactNode;
}

function Field({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  error,
  disabled,
  hint,
  required,
  min,
  max,
  rightElement,
}: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-semibold text-[#3a3750]">
        {label}
        {!required && (
          <span className="ml-1.5 text-[12.5px] font-normal text-[#a6adbf]">
            (optional)
          </span>
        )}
      </label>
      <div className="relative">
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          min={min}
          max={max}
          className={[
            "w-full rounded-2xl border bg-white px-4 py-3.5 text-sm text-[#16213e] placeholder:text-[#a6adbf]",
            "outline-none transition-all focus:ring-4",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error
              ? "border-[#e57373] focus:border-[#e57373] focus:ring-[#e57373]/10"
              : "border-[#dde2ec] focus:border-[#1b2d5e] focus:ring-[#1b2d5e]/12",
            rightElement ? "pr-12" : "",
          ].join(" ")}
        />
        {rightElement && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-4">
            {rightElement}
          </div>
        )}
      </div>
      {hint && !error && <p className="text-[12.5px] text-[#8189a0]">{hint}</p>}
      {error && (
        <p className="flex items-center gap-1.5 text-[12.5px] text-[#e57373]">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#e57373]" />
          {error}
        </p>
      )}
    </div>
  );
}

function ServerError({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-[#fde0d9] bg-[#fff5f3] px-4 py-3.5">
      <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#efb0a4] text-[11.5px] font-bold text-white">
        !
      </span>
      <p className="text-sm leading-5 text-[#b94a3a]">{message}</p>
    </div>
  );
}

function SubmitButton({
  loading,
  children,
}: {
  loading: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="submit"
      disabled={loading}
      className={[
        "flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5",
        "text-sm font-semibold text-white",
        "bg-[#1b2d5e] shadow-[0_8px_24px_rgba(27,45,94,0.35)]",
        "transition-all hover:translate-y-[-1px] hover:bg-[#24397a] hover:shadow-[0_12px_32px_rgba(27,45,94,0.40)]",
        "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0",
      ].join(" ")}
    >
      {loading ? (
        <>
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
            />
          </svg>
          Creating account…
        </>
      ) : (
        children
      )}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────
   CHILD FORM SECTION  (reused inside parent form)
───────────────────────────────────────────────────────────────── */

interface ChildFormData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
}

const emptyChild = (): ChildFormData => ({
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  email: "",
});

interface ChildErrors {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  email?: string;
}

function ChildFormCard({
  index,
  data,
  errors,
  onChange,
  onRemove,
  canRemove,
  disabled,
}: {
  index: number;
  data: ChildFormData;
  errors: ChildErrors;
  onChange: (field: keyof ChildFormData, value: string) => void;
  onRemove: () => void;
  canRemove: boolean;
  disabled: boolean;
}) {
  const trackLabel = data.dateOfBirth ? deriveTrackLabel(data.dateOfBirth) : "";
  const ageOk = data.dateOfBirth ? isValidAge(data.dateOfBirth) : true;

  return (
    <div className="rounded-[22px] border border-[#dde2ec] bg-[#f9fafc] p-5">
      {/* Card header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-[#1b2d5e] text-xs font-bold text-white">
            {index + 1}
          </span>
          <p className="text-sm font-bold text-[#16213e]">
            {data.firstName || `Child ${index + 1}`}
          </p>
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="rounded-xl p-1.5 text-[#c0392b] transition hover:bg-[#fff5f5] disabled:opacity-40"
            aria-label="Remove child"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 4h10M6 4V2.5h4V4M5 4l.5 9M11 4l-.5 9"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          id={`child-${index}-firstName`}
          label="First name"
          value={data.firstName}
          onChange={(v) => onChange("firstName", v)}
          placeholder="e.g. Adaeze"
          error={errors.firstName}
          disabled={disabled}
          required
        />
        <Field
          id={`child-${index}-lastName`}
          label="Last name"
          value={data.lastName}
          onChange={(v) => onChange("lastName", v)}
          placeholder="e.g. Okonkwo"
          error={errors.lastName}
          disabled={disabled}
          required
        />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <Field
            id={`child-${index}-dob`}
            label="Date of birth"
            type="date"
            value={data.dateOfBirth}
            onChange={(v) => onChange("dateOfBirth", v)}
            error={errors.dateOfBirth}
            disabled={disabled}
            max={new Date().toISOString().split("T")[0]}
            required
          />
          {/* Auto-derived track pill */}
          {trackLabel && (
            <div
              className={[
                "mt-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold",
                ageOk
                  ? "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]"
                  : "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
              ].join(" ")}
            >
              {ageOk ? <BookOpen className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> : <TriangleAlert className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />} {trackLabel}
            </div>
          )}
        </div>

        <Field
          id={`child-${index}-email`}
          label="Child's email"
          type="email"
          value={data.email}
          onChange={(v) => onChange("email", v)}
          placeholder="only if they have one"
          hint="Leave blank for young children"
          error={errors.email}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   STEP 1 — ROLE SELECTION
───────────────────────────────────────────────────────────────── */

function RoleSelector({
  onSelect,
}: {
  onSelect: (role: "parent" | "student") => void;
}) {
  return (
    <div className="space-y-4">
      <div className="mb-6 text-center">
        <h1 className="text-[1.7rem] font-bold tracking-[-0.04em] text-[#16213e]">
          Create your account
        </h1>
        <p className="mt-2 text-sm text-[#5f6780]">
          Tell us who is registering so we can show you the right form.
        </p>
      </div>

      <button
        onClick={() => onSelect("parent")}
        className="group flex w-full items-start gap-4 rounded-[22px] border-2 border-[#dde2ec] bg-white p-5 text-left transition hover:border-[#1b2d5e] hover:bg-[#f9fafc]"
      >
        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[14px] bg-[#fff4f1] text-2xl transition group-hover:bg-[#efb0a4]/30">
          <Users className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </span>
        <div>
          <p className="font-bold text-[#16213e]">I am a Parent</p>
          <p className="mt-1 text-sm leading-5 text-[#5f6780]">
            Register your child or children. You manage payments, enrolments,
            and progress from your portal.
          </p>
        </div>
        <span className="ml-auto mt-1 flex-shrink-0 text-[#c9cfe0] transition group-hover:text-[#1b2d5e]">
          ›
        </span>
      </button>

      <button
        onClick={() => onSelect("student")}
        className="group flex w-full items-start gap-4 rounded-[22px] border-2 border-[#dde2ec] bg-white p-5 text-left transition hover:border-[#1b2d5e] hover:bg-[#f9fafc]"
      >
        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[14px] bg-[#ebeff7] text-2xl transition group-hover:bg-[#1b2d5e]/20">
          <Backpack className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </span>
        <div>
          <p className="font-bold text-[#16213e]">I am a Student</p>
          <p className="mt-1 text-sm leading-5 text-[#5f6780]">
            Register yourself directly (ages 13–18). Younger students should ask
            a parent to register on their behalf.
          </p>
        </div>
        <span className="ml-auto mt-1 flex-shrink-0 text-[#c9cfe0] transition group-hover:text-[#1b2d5e]">
          ›
        </span>
      </button>

      <p className="pt-2 text-center text-xs text-[#8189a0]">
        Already have an account?{" "}
        <Link
          to="/login"
          className="font-semibold text-[#1b2d5e] hover:text-[#4a44b0]"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   PARENT FORM
───────────────────────────────────────────────────────────────── */

function ParentForm({
  onBack,
  onSuccess,
}: {
  onBack: () => void;
  onSuccess: (data: { user: any; token: string }) => void;
}) {
  const [register, { isLoading }] = useRegisterMutation();

  // Parent fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  // Children
  const [children, setChildren] = useState<ChildFormData[]>([emptyChild()]);

  // Errors
  const [parentErrors, setParentErrors] = useState<Record<string, string>>({});
  const [childErrors, setChildErrors] = useState<ChildErrors[]>([{}]);
  const [serverError, setServerError] = useState("");

  const updateChild = (
    index: number,
    field: keyof ChildFormData,
    value: string,
  ) => {
    setChildren((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    // Clear that child's field error
    setChildErrors((prev) => {
      const next = [...prev];
      if (next[index]) next[index] = { ...next[index], [field]: undefined };
      return next;
    });
    setServerError("");
  };

  const addChild = () => {
    setChildren((prev) => [...prev, emptyChild()]);
    setChildErrors((prev) => [...prev, {}]);
  };

  const removeChild = (index: number) => {
    setChildren((prev) => prev.filter((_, i) => i !== index));
    setChildErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    const pe: Record<string, string> = {};
    if (!firstName.trim()) pe.firstName = "First name is required";
    if (!lastName.trim()) pe.lastName = "Last name is required";
    if (!email.trim()) pe.email = "Email is required";
    else if (!EMAIL_RE.test(email.trim())) pe.email = "Enter a valid email";
    if (!password) pe.password = "Password is required";
    else if (password.length < 6) pe.password = "At least 6 characters";
    setParentErrors(pe);

    const ce: ChildErrors[] = children.map((c) => {
      const e: ChildErrors = {};
      if (!c.firstName.trim()) e.firstName = "Required";
      if (!c.lastName.trim()) e.lastName = "Required";
      if (!c.dateOfBirth) e.dateOfBirth = "Required";
      else if (!isValidAge(c.dateOfBirth)) e.dateOfBirth = "Age must be 6–18";
      if (c.email && !EMAIL_RE.test(c.email.trim()))
        e.email = "Not a valid email";
      return e;
    });
    setChildErrors(ce);

    const parentOk = Object.keys(pe).length === 0;
    const childrenOk = ce.every((e) => Object.keys(e).length === 0);
    return parentOk && childrenOk;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;

    const payload: ParentRegisterRequest = {
      role: "parent",
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim().toLowerCase(),
      password,
      children: children.map((c) => ({
        firstName: c.firstName.trim(),
        lastName: c.lastName.trim(),
        dateOfBirth: c.dateOfBirth,
        ...(c.email ? { email: c.email.trim().toLowerCase() } : {}),
      })) as ChildRegistrationInput[],
    };

    try {
      const result = await register(payload).unwrap();
      onSuccess({ user: result.user, token: result.token });
    } catch (err: any) {
      const msg =
        err?.data?.error ||
        (err?.data?.details
          ? Array.isArray(err.data.details)
            ? err.data.details.join(" · ")
            : err.data.details
          : null) ||
        "Registration failed. Please try again.";
      setServerError(msg);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-xl border border-[#dde2ec] p-2 text-[#5a6278] transition hover:bg-[#f1f3f8]"
          aria-label="Back"
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
            <path
              d="M10 4L6 8l4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold tracking-[-0.03em] text-[#16213e]">
            Parent registration
          </h1>
          <p className="text-xs text-[#8189a0]">
            Your details first, then your children
          </p>
        </div>
      </div>

      {serverError && <ServerError message={serverError} />}

      {/* Parent details */}
      <div className="rounded-[22px] border border-[#dde2ec] bg-white p-5">
        <p className="mb-4 text-xs font-bold uppercase tracking-wider text-[#8189a0]">
          Your details
        </p>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              id="p-firstName"
              label="First name"
              value={firstName}
              onChange={(v) => {
                setFirstName(v);
                setParentErrors((p) => ({ ...p, firstName: "" }));
              }}
              placeholder="Your first name"
              error={parentErrors.firstName}
              disabled={isLoading}
              required
            />
            <Field
              id="p-lastName"
              label="Last name"
              value={lastName}
              onChange={(v) => {
                setLastName(v);
                setParentErrors((p) => ({ ...p, lastName: "" }));
              }}
              placeholder="Your last name"
              error={parentErrors.lastName}
              disabled={isLoading}
              required
            />
          </div>
          <Field
            id="p-email"
            label="Your email address"
            type="email"
            value={email}
            onChange={(v) => {
              setEmail(v);
              setParentErrors((p) => ({ ...p, email: "" }));
              setServerError("");
            }}
            placeholder="you@example.com"
            error={parentErrors.email}
            disabled={isLoading}
            required
          />
          <Field
            id="p-password"
            label="Password"
            type={showPw ? "text" : "password"}
            value={password}
            onChange={(v) => {
              setPassword(v);
              setParentErrors((p) => ({ ...p, password: "" }));
            }}
            placeholder="At least 6 characters"
            error={parentErrors.password}
            disabled={isLoading}
            required
            rightElement={
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                tabIndex={-1}
                className="text-[#8189a0] hover:text-[#4a44b0]"
              >
                <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
                  {showPw ? (
                    <>
                      <path
                        d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                      />
                      <circle
                        cx="8"
                        cy="8"
                        r="2"
                        stroke="currentColor"
                        strokeWidth="1.3"
                      />
                      <path
                        d="M2 2l12 12"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                      />
                    </>
                  ) : (
                    <>
                      <path
                        d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                      />
                      <circle
                        cx="8"
                        cy="8"
                        r="2"
                        stroke="currentColor"
                        strokeWidth="1.3"
                      />
                    </>
                  )}
                </svg>
              </button>
            }
          />
        </div>
      </div>

      {/* Children */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-wider text-[#8189a0]">
            Children ({children.length})
          </p>
          <button
            type="button"
            onClick={addChild}
            disabled={isLoading || children.length >= 6}
            className="flex items-center gap-1.5 rounded-xl border border-[#dde2ec] px-3 py-1.5 text-xs font-semibold text-[#1b2d5e] transition hover:bg-[#ebeff7] disabled:opacity-40"
          >
            + Add another child
          </button>
        </div>
        <div className="space-y-3">
          {children.map((child, i) => (
            <ChildFormCard
              key={i}
              index={i}
              data={child}
              errors={childErrors[i] ?? {}}
              onChange={(field, value) => updateChild(i, field, value)}
              onRemove={() => removeChild(i)}
              canRemove={children.length > 1}
              disabled={isLoading}
            />
          ))}
        </div>
      </div>

      <SubmitButton loading={isLoading}>Create family account{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></SubmitButton>

      <p className="text-center text-xs text-[#8189a0]">
        Already have an account?{" "}
        <Link
          to="/login"
          className="font-semibold text-[#1b2d5e] hover:text-[#4a44b0]"
        >
          Sign in
        </Link>
      </p>
    </form>
  );
}

/* ─────────────────────────────────────────────────────────────────
   STUDENT FORM
───────────────────────────────────────────────────────────────── */

function StudentForm({
  onBack,
  onSuccess,
}: {
  onBack: () => void;
  onSuccess: (data: { user: any; token: string }) => void;
}) {
  const [register, { isLoading }] = useRegisterMutation();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState("");

  const trackLabel = dateOfBirth ? deriveTrackLabel(dateOfBirth) : "";
  const ageOk = dateOfBirth ? isValidAge(dateOfBirth) : true;

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!firstName.trim()) e.firstName = "First name is required";
    if (!lastName.trim()) e.lastName = "Last name is required";
    if (!email.trim()) e.email = "Email is required";
    else if (!EMAIL_RE.test(email.trim())) e.email = "Enter a valid email";
    if (!password) e.password = "Password is required";
    else if (password.length < 6) e.password = "At least 6 characters";
    if (!dateOfBirth) e.dateOfBirth = "Date of birth is required";
    else if (!isValidAge(dateOfBirth)) e.dateOfBirth = "Age must be 6–18";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;

    const payload: StudentRegisterRequest = {
      role: "student",
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim().toLowerCase(),
      password,
      dateOfBirth,
    };

    try {
      const result = await register(payload).unwrap();
      onSuccess({ user: result.user, token: result.token });
    } catch (err: any) {
      setServerError(
        err?.data?.error || "Registration failed. Please try again.",
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-xl border border-[#dde2ec] p-2 text-[#5a6278] transition hover:bg-[#f1f3f8]"
          aria-label="Back"
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
            <path
              d="M10 4L6 8l4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold tracking-[-0.03em] text-[#16213e]">
            Student registration
          </h1>
          <p className="text-xs text-[#8189a0]">
            For students aged 13–18 with their own email
          </p>
        </div>
      </div>

      {/* Age advisory */}
      <div className="flex items-start gap-3 rounded-[18px] border border-[#dde2ec] bg-[#f9fafc] px-4 py-3.5">
        <span className="text-lg">ℹ️</span>
        <p className="text-xs leading-5 text-[#5a6278]">
          Students aged <strong>6–12</strong> should ask a parent to register on
          their behalf — parents manage payments, progress reports, and
          enrolment. Students <strong>13–18</strong> can register here directly.
        </p>
      </div>

      {serverError && <ServerError message={serverError} />}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          id="s-firstName"
          label="First name"
          value={firstName}
          onChange={(v) => {
            setFirstName(v);
            setErrors((p) => ({ ...p, firstName: "" }));
          }}
          placeholder="Your first name"
          error={errors.firstName}
          disabled={isLoading}
          required
        />
        <Field
          id="s-lastName"
          label="Last name"
          value={lastName}
          onChange={(v) => {
            setLastName(v);
            setErrors((p) => ({ ...p, lastName: "" }));
          }}
          placeholder="Your last name"
          error={errors.lastName}
          disabled={isLoading}
          required
        />
      </div>

      {/* DOB with live track derivation */}
      <div>
        <Field
          id="s-dob"
          label="Date of birth"
          type="date"
          value={dateOfBirth}
          onChange={(v) => {
            setDateOfBirth(v);
            setErrors((p) => ({ ...p, dateOfBirth: "" }));
          }}
          error={errors.dateOfBirth}
          disabled={isLoading}
          max={new Date().toISOString().split("T")[0]}
          required
        />
        {trackLabel && (
          <div
            className={[
              "mt-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold",
              ageOk
                ? "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]"
                : "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
            ].join(" ")}
          >
            {ageOk ? <BookOpen className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> : <TriangleAlert className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />} {trackLabel}
          </div>
        )}
      </div>

      <Field
        id="s-email"
        label="Your email address"
        type="email"
        value={email}
        onChange={(v) => {
          setEmail(v);
          setErrors((p) => ({ ...p, email: "" }));
          setServerError("");
        }}
        placeholder="you@example.com"
        error={errors.email}
        disabled={isLoading}
        required
      />

      <Field
        id="s-password"
        label="Password"
        type={showPw ? "text" : "password"}
        value={password}
        onChange={(v) => {
          setPassword(v);
          setErrors((p) => ({ ...p, password: "" }));
        }}
        placeholder="At least 6 characters"
        error={errors.password}
        disabled={isLoading}
        required
        rightElement={
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            tabIndex={-1}
            className="text-[#8189a0] hover:text-[#4a44b0]"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              {showPw ? (
                <>
                  <path
                    d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="8"
                    cy="8"
                    r="2"
                    stroke="currentColor"
                    strokeWidth="1.3"
                  />
                  <path
                    d="M2 2l12 12"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                </>
              ) : (
                <>
                  <path
                    d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="8"
                    cy="8"
                    r="2"
                    stroke="currentColor"
                    strokeWidth="1.3"
                  />
                </>
              )}
            </svg>
          </button>
        }
      />

      <SubmitButton loading={isLoading}>Create student account{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></SubmitButton>

      <p className="text-center text-xs text-[#8189a0]">
        Already have an account?{" "}
        <Link
          to="/login"
          className="font-semibold text-[#1b2d5e] hover:text-[#4a44b0]"
        >
          Sign in
        </Link>
      </p>
    </form>
  );
}

/* ─────────────────────────────────────────────────────────────────
   DECORATIVE LEFT PANEL  (same as login)
───────────────────────────────────────────────────────────────── */

function LeftPanel() {
  return (
    <div className="relative hidden flex-col justify-between overflow-hidden bg-[#16213e] p-10 lg:flex lg:w-[46%]">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(27,45,94,0.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(27,45,94,0.07) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px",
        }}
      />
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-16 -top-16 h-72 w-72 rounded-full bg-[#1b2d5e]/20 blur-3xl" />
        <div className="absolute -bottom-12 right-4 h-56 w-56 rounded-full bg-[#efb0a4]/15 blur-3xl" />
      </div>

      <div className="relative z-10 flex items-center gap-2.5">
        <img src="/images/logo.png" alt="" className="h-10 w-10 rounded-[12px]" />
        <div>
          <p className="text-sm font-bold tracking-[-0.03em] text-white">
            Future Smart Lab
          </p>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">
            Innovation Academy
          </p>
        </div>
      </div>

      <div className="relative z-10 space-y-3">
        {[
          { icon: Hammer, label: "Build real technology" },
          { icon: Bot, label: "Apply AI to solve problems" },
          { icon: Briefcase, label: "Learn venture & entrepreneurship" },
          { icon: Mic, label: "Present publicly every quarter" },
          { icon: Coins, label: "Build financial literacy" },
        ].map((s) => (
          <div
            key={s.label}
            className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm"
          >
            <span className="text-base"><Ico icon={s.icon} /></span>
            <span className="text-sm font-medium text-white/75">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="relative z-10 rounded-[20px] border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
        <p className="text-[15px] font-medium leading-7 text-white/85">
          "The next wave of Nigerian founders is currently between 6 and 18.
          Future Smart Lab exists to find them, form them, and equip them."
        </p>
        <p className="mt-3 text-xs font-semibold uppercase tracking-widest text-[#1b2d5e]">
          — FSL Mission
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   ROOT EXPORT
───────────────────────────────────────────────────────────────── */

type Step = "choose" | "parent" | "student";

export default function Register() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [step, setStep] = useState<Step>("choose");

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate("/user/dashboard", { replace: true });
  }, [isAuthenticated, navigate]);

  const handleSuccess = ({ user, token }: { user: any; token: string }) => {
    dispatch(setCredentials({ user, token }));
    navigate("/user/dashboard", { replace: true });
  };

  return (
    <div className="flex min-h-screen">
      <Seo title="Create your account" description="Create your Future Smart Lab parent or student account to manage enrolment, progress and showcases." path="/register" noindex />
      <LeftPanel />

      {/* Right panel */}
      <div className="flex flex-1 flex-col items-center justify-start bg-[#f7f8fb] px-5 py-10 sm:px-8">
        {/* Mobile logo */}
        <div className="mb-6 self-start lg:hidden">
          <FSLMark />
        </div>

        <div className="w-full max-w-[480px]">
          <div className="rounded-[28px] border border-[#dde2ec] bg-white p-6 shadow-[0_20px_60px_rgba(27,45,94,0.09)] sm:p-8">
            {step === "choose" && (
              <RoleSelector onSelect={(role) => setStep(role)} />
            )}
            {step === "parent" && (
              <ParentForm
                onBack={() => setStep("choose")}
                onSuccess={handleSuccess}
              />
            )}
            {step === "student" && (
              <StudentForm
                onBack={() => setStep("choose")}
                onSuccess={handleSuccess}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
